import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AtSign, Send, Users } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

/**
 * DebtCaseChatPanel — internal Slack-style chat pinned to a debt case.
 *
 * Visible ONLY to debt-team members (backend enforces). The borrower
 * never sees this back-channel. Supports @email mentions with a
 * typeahead. Polls the case chat every 12s.
 */
function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function renderBody(body, mentions) {
  if (!body) return null;
  // Highlight @email tokens.
  const parts = body.split(/(@[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g);
  return parts.map((p, i) =>
    p.startsWith("@")
      ? <span key={i} className="text-navy font-semibold bg-yellow-400/25 px-1 rounded">{p}</span>
      : <span key={i}>{p}</span>
  );
}

export default function DebtCaseChatPanel({ caseId, caseUid }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [posting, setPosting] = useState(false);
  const scrollerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/debt/cases/${caseId}/chat`);
      setMessages(r.data?.items || []);
    } catch { /* silent — probably 403 for non-debt-team user */ }
  };

  useEffect(() => {
    if (!caseId) return;
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Auto-scroll to newest message.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  // Typeahead — fetch candidates when the last "@" fragment changes.
  useEffect(() => {
    const m = body.match(/@([a-zA-Z0-9._+-]*)$/);
    if (m) {
      setShowMentionPicker(true);
      setMentionQuery(m[1] || "");
      api.get(`/debt/cases/${caseId}/chat/mention-candidates`, { params: { q: m[1] || undefined } })
        .then((r) => setCandidates(r.data?.items || []))
        .catch(() => setCandidates([]));
    } else {
      setShowMentionPicker(false);
    }
  }, [body, caseId]);

  const insertMention = (email) => {
    const next = body.replace(/@[a-zA-Z0-9._+-]*$/, `@${email} `);
    setBody(next);
    setShowMentionPicker(false);
  };

  const post = async () => {
    const text = body.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post(`/debt/cases/${caseId}/chat`, { body: text });
      setBody("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to post message"));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-case-chat">
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-2">
          <Users size={12} /> Team Chat
          <span className="text-[10px] font-mono text-ink-muted normal-case tracking-normal">
            Internal · not visible to the borrower
          </span>
        </div>
        <div className="text-[10px] font-mono text-ink-muted">
          {messages.length} message{messages.length === 1 ? "" : "s"}
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="space-y-2 mb-3 max-h-[320px] overflow-y-auto pr-1"
        data-testid="debt-chat-scroller"
      >
        {messages.length === 0 && (
          <div className="text-xs italic text-ink-muted">No chatter yet. Mention a teammate with @email so this doesn't drift to WhatsApp.</div>
        )}
        {messages.map((m) => {
          const isMe = m.author_user_id === user?.user_id;
          return (
            <div
              key={m.message_id}
              className={`flex ${isMe ? "justify-end" : "justify-start"}`}
              data-testid={`debt-chat-msg-${m.message_id}`}
            >
              <div className={`max-w-[80%] rounded-lg px-3 py-2 border ${
                isMe ? "bg-navy/[0.06] border-navy/15" : "bg-yellow-400/[0.08] border-yellow-500/25"
              }`}>
                <div className="text-[10px] font-mono text-ink-muted mb-0.5 flex items-center gap-1.5">
                  <span className="font-semibold text-ink">{m.author_name || m.author_email}</span>
                  <span className="text-ink-muted/60">·</span>
                  <span>{m.author_role}</span>
                  <span className="text-ink-muted/60">·</span>
                  <span>{formatWhen(m.at)}</span>
                </div>
                <div className="text-[13px] text-ink whitespace-pre-wrap break-words">
                  {renderBody(m.body, m.mentions)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative">
        <div className="flex gap-2 items-end">
          <div className="flex-1 relative">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); }
              }}
              placeholder="Type a message. Use @teammate@venturaz.com to ping someone. Cmd/Ctrl+Enter to send."
              rows={2}
              className="w-full px-3 py-2 border border-navy/15 rounded text-sm resize-none focus:outline-none focus:ring-1 focus:ring-navy"
              data-testid="debt-chat-input"
            />
            {showMentionPicker && candidates.length > 0 && (
              <div
                className="absolute bottom-full mb-1 left-0 w-[320px] max-h-[220px] overflow-y-auto bg-white border border-navy/20 rounded-md shadow-[0_18px_40px_rgba(15,61,46,0.14)] z-20"
                data-testid="debt-chat-mention-picker"
              >
                <div className="px-3 py-2 border-b border-ink/10 text-[10px] font-mono uppercase tracking-widest text-navy flex items-center gap-1">
                  <AtSign size={11} /> Mention a teammate
                </div>
                {candidates.map((c) => (
                  <button
                    type="button"
                    key={c.user_id}
                    onClick={() => insertMention(c.email)}
                    className="w-full text-left px-3 py-2 hover:bg-navy/5 group flex items-center justify-between"
                    data-testid={`debt-chat-mention-${c.user_id}`}
                  >
                    <div>
                      <div className="text-sm font-heading text-ink group-hover:text-navy">{c.name || c.email}</div>
                      <div className="text-[11px] text-ink-muted">{c.email}</div>
                    </div>
                    <div className="text-[10px] font-mono text-ink-muted">{c.role}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={post}
            disabled={posting || !body.trim()}
            className="px-4 py-2 rounded bg-navy text-white text-sm font-heading font-semibold hover:bg-[#256E58] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid="debt-chat-send"
          >
            <Send size={13} /> {posting ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
